import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Button,
  Stack,
  Divider,
  Avatar
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as CartIcon,
  Discount as DiscountIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Undo as UndoIcon,
  LocalOffer as OfferIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
  AccountBalanceWallet as WalletIcon,
  PointOfSale as CashIcon,
  AccountBalance as TransferIcon
} from '@mui/icons-material';
import StatsService from '../../services/stats';
import SaleService from '../../services/sales';
import { ccyFormat } from '../../helpers/helper';
import SellerSalesModal from './SellerSalesModal';
import SalesCharts from './SalesCharts';
import WeeklyAnalysis from './WeeklyAnalysis';
import GrowthAnalysis from './GrowthAnalysis';
import { CircularProgressCustom } from '../../components';

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      borderRadius: 3,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${color}40`,
      }
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} color={color} mt={1} mb={0.5}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Chip
              label={trend}
              size="small"
              sx={{
                mt: 1,
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                background: `${color}20`,
                color: color
              }}
            />
          )}
        </Box>
        <Avatar
          sx={{
            bgcolor: `${color}20`,
            color: color,
            width: 56,
            height: 56
          }}
        >
          <Icon fontSize="large" />
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const SalesStats = ({ storeId }) => {
  const now = new Date();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start_date: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0'),
    end_date: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0'),
  });
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerSales, setSellerSales] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (storeId) {
        try {
          setLoading(true);
          const data = await StatsService.getSalesStats(storeId);
          console.log('Stats data received:', data);
          setStats(data);
        } catch (error) {
          console.error('Error loading stats:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    loadData();
  }, [storeId]);

  const loadStats = async (params = {}) => {
    try {
      setLoading(true);
      const data = await StatsService.getSalesStats(storeId, params);
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterByDate = () => {
    loadStats(dateRange);
  };

  const handleResetFilter = () => {
    setDateRange({ start_date: '', end_date: '' });
    loadStats();
  };

  const handleSellerClick = async (seller) => {
    setSelectedSeller(seller);
    setOpenModal(true);
    setLoadingSales(true);
    
    try {
      const query = new URLSearchParams();
      query.append('user_id', seller.user_id);
      
      // Agregar rango de fechas si existe
      if (dateRange.start_date) {
        query.append('date', dateRange.start_date);
      }
      if (dateRange.end_date) {
        query.append('dateTo', dateRange.end_date);
      }
      
      const sales = await SaleService.searchSales(storeId, query.toString());
      setSellerSales(sales || []);
    } catch (error) {
      console.error('Error loading seller sales:', error);
      setSellerSales([]);
    } finally {
      setLoadingSales(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSeller(null);
    setSellerSales([]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgressCustom size={200} />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">
          No hay datos disponibles
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Dashboard de Ventas
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Estadísticas y análisis de rendimiento
        </Typography>
        {stats.period && (
          <Chip
            label={`Período: ${stats.period}`}
            icon={<ReceiptIcon />}
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {/* Filtros de Fecha */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Filtrar por Período
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Fecha Inicio"
            type="date"
            value={dateRange.start_date}
            onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Fecha Fin"
            type="date"
            value={dateRange.end_date}
            onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={handleFilterByDate}
            sx={{ minWidth: 120, height: 56 }}
          >
            Filtrar
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetFilter}
            sx={{ minWidth: 120, height: 56 }}
          >
            Resetear
          </Button>
        </Stack>
      </Paper>

      {/* Métricas Principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Ventas"
            value={stats.total_sales}
            subtitle="Operaciones realizadas"
            icon={CartIcon}
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Ingresos Totales"
            value={`$${ccyFormat(stats.total_revenue)}`}
            subtitle="Revenue generado"
            icon={MoneyIcon}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Ticket Promedio"
            value={`$${ccyFormat(stats.average_ticket)}`}
            subtitle="Por transacción"
            icon={ReceiptIcon}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unidades Vendidas"
            value={stats.total_quantity_sold}
            subtitle="Productos en total"
            icon={TrendingUpIcon}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Descuentos Totales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Descuento Total"
            value={`$${ccyFormat(stats.total_discount || 0)}`}
            subtitle="Descuentos aplicados"
            icon={DiscountIcon}
            color="#e91e63"
          />
        </Grid>
      </Grid>

      {/* Estado de Ventas */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Estado de Ventas
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #4caf5015 0%, #4caf5005 100%)',
                    border: '1px solid #4caf5030'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <CheckCircleIcon sx={{ color: '#4caf50' }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Finalizadas
                    </Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={700} color="#4caf50">
                    {stats.sales_finalized}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${ccyFormat(stats.revenue_finalized)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #ff980015 0%, #ff980005 100%)',
                    border: '1px solid #ff980030'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <ScheduleIcon sx={{ color: '#ff9800' }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Reservas
                    </Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={700} color="#ff9800">
                    {stats.sales_reserved}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${ccyFormat(stats.revenue_reserved)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f4433615 0%, #f4433605 100%)',
                    border: '1px solid #f4433630'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <CancelIcon sx={{ color: '#f44336' }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Cancelaciones
                    </Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={700} color="#f44336">
                    {stats.sales_canceled}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Operaciones
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #2196f315 0%, #2196f305 100%)',
                    border: '1px solid #2196f330'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <UndoIcon sx={{ color: '#2196f3' }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Devueltas
                    </Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={700} color="#2196f3">
                    {stats.sales_returned || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Operaciones
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Ofertas y Descuentos
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                mb: 2
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <OfferIcon sx={{ color: 'white' }} />
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  Ventas con Ofertas
                </Typography>
              </Stack>
              <Typography variant="h3" fontWeight={700} color="white">
                {stats.sales_with_offers}
              </Typography>
              <Typography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                de {stats.total_sales} ventas totales
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <DiscountIcon sx={{ color: 'white' }} />
                <Typography variant="subtitle2" fontWeight={600} color="white">
                  Ahorro Total
                </Typography>
              </Stack>
              <Typography variant="h3" fontWeight={700} color="white">
                ${ccyFormat(stats.total_savings_offers)}
              </Typography>
              <Typography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                En descuentos aplicados
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Depósitos y Señas */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Depósitos y Señas
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <WalletIcon sx={{ color: 'white' }} />
                    <Typography variant="subtitle2" fontWeight={600} color="white">
                      Ventas con Depósito
                    </Typography>
                  </Stack>
                  <Typography variant="h3" fontWeight={700} color="white">
                    {stats.sales_with_deposit || 0}
                  </Typography>
                  <Typography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                    de {stats.total_sales} ventas totales
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <MoneyIcon sx={{ color: 'white' }} />
                    <Typography variant="subtitle2" fontWeight={600} color="white">
                      Total Depósitos/Señas
                    </Typography>
                  </Stack>
                  <Typography variant="h3" fontWeight={700} color="white">
                    ${ccyFormat(stats.total_deposits || 0)}
                  </Typography>
                  <Typography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                    Monto total en señas
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Finalizadas por Medio de Pago */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Ventas Finalizadas por Medio de Pago
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #2e7d3215 0%, #2e7d3205 100%)',
                    border: '1px solid #2e7d3230'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <CashIcon sx={{ color: '#2e7d32' }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Efectivo
                    </Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={700} color="#2e7d32">
                    {stats.finalized_cash_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${ccyFormat(stats.finalized_cash_total || 0)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #1565c015 0%, #1565c005 100%)',
                    border: '1px solid #1565c030'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <TransferIcon sx={{ color: '#1565c0' }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Transferencia
                    </Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={700} color="#1565c0">
                    {stats.finalized_transfer_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${ccyFormat(stats.finalized_transfer_total || 0)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráficos de Análisis */}
      <SalesCharts stats={stats} />

      {/* Análisis de Crecimiento */}
      <GrowthAnalysis stats={stats} />

      {/* Análisis Semanal */}
      <WeeklyAnalysis stats={stats} />

      {/* Top Vendedores */}
      {stats.top_sellers && stats.top_sellers.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <TrophyIcon sx={{ color: '#ffd700', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={600}>
              Top Vendedores
            </Typography>
          </Stack>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            {stats.top_sellers.map((seller, index) => (
              <Grid item xs={12} sm={6} md={4} key={seller.user_id}>
                <Card 
                  elevation={0}
                  onClick={() => handleSellerClick(seller)}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'visible',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  {index < 3 && (
                    <Chip
                      icon={<TrophyIcon />}
                      label={`#${index + 1}`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 16,
                        fontWeight: 700,
                        background: index === 0
                          ? 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)'
                          : index === 1
                          ? 'linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)'
                          : 'linear-gradient(135deg, #cd7f32 0%, #e8a87c 100%)',
                        color: index === 2 ? 'white' : '#333'
                      }}
                    />
                  )}
                  {index >= 3 && (
                    <Chip
                      label={`#${index + 1}`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 16,
                        fontWeight: 600,
                        bgcolor: '#e0e0e0'
                      }}
                    />
                  )}
                  <CardContent sx={{ pt: 3 }}>
                    <Stack spacing={2}>
                      {/* Avatar y nombre */}
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          src={seller.picture}
                          alt={seller.user_name}
                          sx={{
                            width: 64,
                            height: 64,
                            border: '3px solid',
                            borderColor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#e0e0e0'
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={700} noWrap>
                            {seller.user_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {seller.user_id}
                          </Typography>
                        </Box>
                      </Stack>

                      <Divider />

                      {/* Métricas */}
                      <Stack spacing={1.5}>
                        {/* Ventas */}
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: '#f5f5f5',
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Ventas
                            </Typography>
                            <Chip
                              label={seller.sales}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white'
                              }}
                            />
                          </Stack>
                        </Box>

                        {/* Revenue */}
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: '#f5f5f5',
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Ingresos
                            </Typography>
                            <Typography variant="body1" fontWeight={700} color="success.main">
                              ${ccyFormat(seller.revenue)}
                            </Typography>
                          </Stack>
                        </Box>

                        {/* Ticket Promedio */}
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                            border: '1px solid #667eea30'
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Ticket Promedio
                            </Typography>
                            <Typography variant="body1" fontWeight={700} color="#667eea">
                              ${ccyFormat(seller.avg_ticket)}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Top Artículos */}
      {stats.top_articles && stats.top_articles.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Top Artículos Vendidos
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Posición</strong></TableCell>
                  <TableCell><strong>Artículo</strong></TableCell>
                  <TableCell><strong>SKU</strong></TableCell>
                  <TableCell align="right"><strong>Cantidad</strong></TableCell>
                  <TableCell align="right"><strong>Ventas</strong></TableCell>
                  <TableCell align="right"><strong>Revenue</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.top_articles.map((article, index) => (
                  <TableRow
                    key={article.article_id}
                    sx={{
                      '&:hover': { bgcolor: '#f5f5f5' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          background: index === 0
                            ? 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)'
                            : index === 1
                            ? 'linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)'
                            : index === 2
                            ? 'linear-gradient(135deg, #cd7f32 0%, #e8a87c 100%)'
                            : '#e0e0e0'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {article.article_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {article.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={article.quantity}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {article.sales}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        ${ccyFormat(article.revenue)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Modal de Detalle de Ventas del Vendedor */}
      <SellerSalesModal
        open={openModal}
        onClose={handleCloseModal}
        seller={selectedSeller}
        sales={sellerSales}
        loading={loadingSales}
        dateRange={dateRange}
      />
    </Box>
  );
};

export default SalesStats;
